"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WaveLeaderboardGalleryItem } from "./WaveLeaderboardGalleryItem";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { useWaveDropsLeaderboard } from "@/hooks/useWaveDropsLeaderboard";
interface WaveLeaderboardGalleryProps {
  readonly wave: ApiWave;
  readonly sort: WaveDropsLeaderboardSort;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly curatedByGroupId?: string | undefined;
  readonly minPrice?: number | undefined;
  readonly maxPrice?: number | undefined;
  readonly priceCurrency?: string | undefined;
}
export const WaveLeaderboardGallery: React.FC<WaveLeaderboardGalleryProps> = t0 => {
  const $ = _c(39);
  const {
    wave,
    sort,
    isVotingClosed: t1,
    isVotingControlsLocked: t2,
    onDropClick,
    curatedByGroupId,
    minPrice,
    maxPrice,
    priceCurrency
  } = t0;
  const isVotingClosed = t1 === undefined ? false : t1;
  const isVotingControlsLocked = t2 === undefined ? false : t2;
  const winningThreshold = wave.wave.type === ApiWaveType.Approve ? wave.wave.winning_threshold : null;
  const winningThresholdMinDurationMs = wave.wave.type === ApiWaveType.Approve ? wave.wave.winning_threshold_min_duration_ms : null;
  let t3;
  if ($[0] !== curatedByGroupId || $[1] !== maxPrice || $[2] !== minPrice || $[3] !== priceCurrency || $[4] !== sort || $[5] !== wave.id) {
    t3 = {
      waveId: wave.id,
      sort,
      curatedByGroupId,
      minPrice,
      maxPrice,
      priceCurrency
    };
    $[0] = curatedByGroupId;
    $[1] = maxPrice;
    $[2] = minPrice;
    $[3] = priceCurrency;
    $[4] = sort;
    $[5] = wave.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useWaveDropsLeaderboard(t3);
  const [animationKey, setAnimationKey] = React.useState(0);
  const [previousSort, setPreviousSort] = React.useState(sort);
  let t4;
  let t5;
  if ($[7] !== previousSort || $[8] !== sort) {
    t4 = () => {
      if (previousSort !== sort) {
        setPreviousSort(sort);
        setAnimationKey(_temp);
      }
    };
    t5 = [sort, previousSort];
    $[7] = previousSort;
    $[8] = sort;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[11] !== drops) {
    t6 = drops.filter(_temp2) || [];
    $[11] = drops;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const dropsWithMedia = t6;
  if (isFetching && dropsWithMedia.length === 0) {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="tw-flex tw-h-32 tw-items-center tw-justify-center"><div className="tw-text-sm tw-text-iron-500">Loading drops...</div></div>;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  if (dropsWithMedia.length === 0) {
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="tw-flex tw-h-32 tw-items-center tw-justify-center tw-text-sm tw-text-iron-500">No drops to show</div>;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  let t7;
  if ($[15] !== animationKey || $[16] !== dropsWithMedia || $[17] !== isVotingClosed || $[18] !== isVotingControlsLocked || $[19] !== onDropClick || $[20] !== sort || $[21] !== winningThreshold || $[22] !== winningThresholdMinDurationMs) {
    let t8;
    if ($[24] !== animationKey || $[25] !== isVotingClosed || $[26] !== isVotingControlsLocked || $[27] !== onDropClick || $[28] !== sort || $[29] !== winningThreshold || $[30] !== winningThresholdMinDurationMs) {
      t8 = drop_0 => <WaveLeaderboardGalleryItem key={drop_0.id} drop={drop_0} onDropClick={onDropClick} activeSort={sort} animationKey={animationKey} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} />;
      $[24] = animationKey;
      $[25] = isVotingClosed;
      $[26] = isVotingControlsLocked;
      $[27] = onDropClick;
      $[28] = sort;
      $[29] = winningThreshold;
      $[30] = winningThresholdMinDurationMs;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    t7 = dropsWithMedia.map(t8);
    $[15] = animationKey;
    $[16] = dropsWithMedia;
    $[17] = isVotingClosed;
    $[18] = isVotingControlsLocked;
    $[19] = onDropClick;
    $[20] = sort;
    $[21] = winningThreshold;
    $[22] = winningThresholdMinDurationMs;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[32] !== fetchNextPage || $[33] !== hasNextPage || $[34] !== isFetchingNextPage) {
    t8 = hasNextPage && <div className="tw-col-span-full tw-mb-2 tw-mt-4 tw-flex tw-justify-center"><button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-text-iron-400 tw-transition desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-300">{isFetchingNextPage ? "Loading more..." : "Load more drops"}</button></div>;
    $[32] = fetchNextPage;
    $[33] = hasNextPage;
    $[34] = isFetchingNextPage;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t7 || $[37] !== t8) {
    t9 = <div className="tw-w-full tw-min-w-0 tw-@container"><div className="tw-grid tw-w-full tw-min-w-0 tw-items-stretch tw-gap-x-4 tw-gap-y-8 @lg:tw-grid-cols-2 @3xl:tw-grid-cols-3">{t7}{t8}</div></div>;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  return t9;
};
function _temp(prev) {
  return prev + 1;
}
function _temp2(drop) {
  return (drop.parts[0]?.media.length ?? 0) > 0;
}